import { computed, ComputedRef, useContext, useRoute } from '@nuxtjs/composition-api';
import { DocumentNode } from '@shared/components/RichTextDisplay/types';
import { slugify } from '@shared/helpers/slugify';
import { PageMetaData } from '@shared/types/storyblok';
import { IUCHeroGalleryItem } from '@shared/types/UCHeroGallery';
import {
  getStoryblokImageURL,
  getStoryBlokVersion,
  useContent,
} from '@unified-commerce/gpc-vue-storefront-storyblok';

interface BrandContent {
  fullSlug: ComputedRef<string>;
  name: ComputedRef<string>;
  description: ComputedRef<string | DocumentNode>;
  logoSrc: ComputedRef<string>;
  logoAlt: ComputedRef<string>;
  banners: ComputedRef<IUCHeroGalleryItem[]>;
  metafields: ComputedRef<PageMetaData>;
}

interface UseBrandContent {
  load: (vendor: string) => Promise<void>;
  loading: ComputedRef<boolean>;
  error: ComputedRef<string | null>;
  brand: BrandContent;
}

const useBrandContent = (): UseBrandContent => {
  const route = useRoute();
  const { $config } = useContext();
  const version = getStoryBlokVersion($config.ENV, route.value);
  const { slug } = route.value.params;
  const { search, content = {}, loading, error } = useContent(`brandSearch-${slug}`);

  // Supports both BrandPage schema (e.g., see AMX vs Resto in Storyblok)
  const brandContent = computed(
    () => content.value?.content?.[0]?.brand?.content ?? content.value?.brand?.content,
  );
  const metafields = computed(() => content.value?.metafields);

  const fullSlug = computed(() => content.value?._meta?.full_slug ?? '');
  const name = computed(() => brandContent.value?.name || '');
  const description = computed(() => brandContent.value?.description);
  const banners = computed(() => brandContent.value?.banner?.[0]?.slides ?? []);
  const logoSrc = computed(() => getStoryblokImageURL(brandContent.value?.logo?.filename));
  const logoAlt = computed(() =>
    brandContent.value?.logo?.alt
      ? brandContent.value?.logo?.alt
      : `${brandContent.value?.name} logo`,
  );

  const load = async (vendor: string) => {
    if (!vendor || loading.value) return;
    await search({
      version,
      url: `brands/${slugify(vendor)}`,
      relations: 'BrandHero.brand_video,BrandHero.brand,BrandHero.banner,BrandPage.brand',
      optimisticStoryLoading: true,
    });
  };

  return {
    load,
    loading: computed(() => loading.value),
    error: computed(() => error.value?.search?.message ?? null),
    brand: {
      fullSlug,
      name,
      description,
      banners,
      logoSrc,
      logoAlt,
      metafields,
    },
  };
};

export default useBrandContent;
